/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The one next thing.
 *
 * ── THE PROBLEM THIS SOLVES ─────────────────────────────────────────────────
 * Every screen in this app was an accurate inventory of what that screen could
 * do, and almost none of them said what to do NEXT. A new household landed on a
 * dashboard of empty panels; an established one landed on a dashboard of full
 * panels; in both cases the app declined to have an opinion, so the user had to
 * hold the weekly loop — set up, plan, list, shop, cook — in their own head.
 *
 * This card is the app's opinion. It renders exactly ONE recommended action,
 * always in the same place, always the most prominent thing on the screen, and
 * it is the only `btn-primary` on Today.
 *
 * ── WHY A LADDER AND NOT A SCORE ────────────────────────────────────────────
 * The rule is a deterministic top-down ladder, not a ranking or a heuristic.
 * That matters more than it sounds: a user learns this card by watching it
 * change, and something that can't be predicted can't be learned. The same
 * household state always produces the same recommendation, so "it told me to
 * plan Thursday, I planned Thursday, now it's telling me to make the list"
 * reads as a system with a plan rather than a widget with a mood.
 *
 * ── NO NEW DATA ─────────────────────────────────────────────────────────────
 * Every input is already in the /api/home payload: `setup`, `week`,
 * `emptyDaysThisWeek`, `shoppingList`, `meals`. This is presentation. There is
 * no new endpoint, and — importantly — no `new Date()`: the "first empty day"
 * is picked from the server's own week array, which the server built in the
 * household's timezone.
 */

import { Link } from "react-router-dom";

import type { HomeResponse } from "../../api/types.ts";
import { Icon, type IconName } from "../../components/Icon.tsx";
import { formatWeekday } from "../../lib/dates.ts";
import { usePrefs } from "../../i18n/index.tsx";

interface Next {
  /** Small uppercase line above the headline. */
  eyebrow: string;
  title: string;
  body: string;
  cta: string;
  to: string;
  icon: IconName;
  /** The steady state is stated calmly, not sold. */
  calm?: boolean;
}

export function computeNext(data: HomeResponse, lang: string): Next {
  const { setup, week, emptyDaysThisWeek, shoppingList, meals } = data;

  // ── 0. Nothing done at all ────────────────────────────────────────────────
  // Offer the wizard, which does all four setup steps and hands back a planned
  // week with a shopping list. Deliberately an OFFER on this card rather than a
  // forced redirect on sign-in: an onboarding flow you cannot decline is a
  // trap, and the four steps below remain available to anyone who'd rather do
  // it themselves.
  if (setup.remaining === setup.steps.length) {
    return {
      eyebrow: "Welcome",
      title: "Let's set up your kitchen",
      body: "Answer three questions and you'll get a planned week with a shopping list "
        + "already built — roughly a minute, and much easier to edit than to start from nothing.",
      cta: "Get started",
      to: "/welcome",
      icon: "sparkles",
    };
  }

  // ── 1–2. Setup, in the only order that works ──────────────────────────────
  // You cannot plan a meal before you have a recipe, and a list built from an
  // empty plan is an empty list. The server already computes `setup.next`.
  if (setup.next === "eaters") {
    return {
      eyebrow: "First things first",
      title: "Who's eating?",
      body: "Add everyone in the household and anything they can't eat. "
        + "It's what lets the app warn you before you cook something with peanuts in it.",
      cta: "Add people",
      to: "/family",
      icon: "users",
    };
  }

  if (setup.next === "recipes") {
    return {
      eyebrow: "Next",
      title: "Add your first recipe",
      body: "Paste a link to any recipe on the web and it'll be read for you — "
        + "or photograph a cookbook page, write one yourself, or have one generated.",
      cta: "Add a recipe",
      to: "/recipes?add=1",
      icon: "book",
    };
  }

  // ── 3. An empty day this week ─────────────────────────────────────────────
  // Named, not counted. "Nothing planned for Thursday" is a task; "3 empty
  // days" is a statistic, and statistics don't get acted on.
  if (emptyDaysThisWeek > 0) {
    const todayIdx = week.findIndex((d) => d.isToday);
    const from = todayIdx === -1 ? 0 : todayIdx;
    const firstEmpty = week.slice(from).find((d) => d.count === 0) ?? week.find((d) => d.count === 0);
    if (firstEmpty) {
      const dayName = formatWeekday(firstEmpty.date, lang);
      return {
        eyebrow: "Next",
        title: `Nothing planned for ${dayName}`,
        body: emptyDaysThisWeek === 1
          ? "One gap left in the week."
          : `${emptyDaysThisWeek} days still to fill. Start with ${dayName}.`,
        cta: `Plan ${dayName}`,
        to: `/plan?date=${firstEmpty.date}&add=1`,
        icon: "calendar",
      };
    }
  }

  const hasPlannedMeals = week.some((d) => d.count > 0);

  // ── 4. Planned, but nothing to shop from ──────────────────────────────────
  if (hasPlannedMeals && !shoppingList) {
    return {
      eyebrow: "Next",
      title: "Make this week's shopping list",
      body: "Everything your planned meals need, combined and sorted by aisle. "
        + "Cupboard staples are left off.",
      cta: "Make the list",
      to: "/shopping?generate=1",
      icon: "cart",
    };
  }

  // ── 5. A list with work left in it ────────────────────────────────────────
  if (shoppingList && shoppingList.openItems > 0) {
    return {
      eyebrow: "Ready when you are",
      title: `${shoppingList.openItems} thing${shoppingList.openItems === 1 ? "" : "s"} left to buy`,
      body: `${shoppingList.title} — sorted by aisle, works offline, and ticks off `
        + "in real time if someone else is shopping it too.",
      cta: "Start shopping",
      to: `/shopping/${shoppingList.id}/mode`,
      icon: "cart",
    };
  }

  // ── 6. Something to cook today ────────────────────────────────────────────
  const uncooked = meals.find((m) => m.status === "planned");
  if (uncooked) {
    return {
      eyebrow: "Tonight",
      title: uncooked.title,
      body: [
        uncooked.totalMinutes ? `${uncooked.totalMinutes} minutes` : null,
        `${uncooked.servings} servings`,
      ].filter(Boolean).join(" · "),
      cta: uncooked.recipeId ? "Open the recipe" : "Open the plan",
      to: uncooked.recipeId ? `/recipes/${uncooked.recipeId}` : "/plan",
      icon: "clock",
    };
  }

  // ── 7. Steady state ───────────────────────────────────────────────────────
  return {
    eyebrow: "All done",
    title: "This week is sorted",
    body: "Everything's planned, bought and cooked. Get a head start on next week "
      + "whenever you fancy.",
    cta: "Plan next week",
    to: "/plan?week=next",
    icon: "check",
    calm: true,
  };
}

export function NextCard({ data }: { data: HomeResponse }) {
  const { lang } = usePrefs();
  const next = computeNext(data, lang);

  return (
    <section
      aria-label="What to do next"
      className={`card p-5 sm:p-6 ${
        next.calm ? "border-rule-soft" : "border-accent/40 bg-accent/5"}`}
    >
      <div className="flex items-start gap-4">
        <span
          aria-hidden
          className={`hidden h-11 w-11 shrink-0 place-items-center rounded-xl sm:grid ${
            next.calm ? "bg-paper-3 text-ink-3" : "bg-accent/15 text-accent"}`}
        >
          <Icon name={next.icon} className="h-5 w-5" />
        </span>

        <div className="min-w-0 flex-1">
          <div className={`label-mono text-[11px] ${next.calm ? "text-ink-3" : "text-accent"}`}>
            {next.eyebrow}
          </div>
          <h2 className="type-title mt-1 sm:text-2xl">
            {next.title}
          </h2>
          <p className="mt-1.5 max-w-prose text-sm text-ink-2">{next.body}</p>

          {/* The only btn-primary on this screen. That is the point of it. */}
          <Link to={next.to} className="btn-primary mt-4 w-full sm:w-auto">
            {next.cta}
            <Icon name="chevronRight" className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
